import { create } from 'zustand';
import { RegisterAcceptedResponse, User } from '../types/auth.types';
import { authApi } from '../api/auth.api';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<RegisterAcceptedResponse>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

function getStatusCode(error: any): number | undefined {
  return error?.response?.status;
}

function getApiMessage(error: any): string | undefined {
  return error?.response?.data?.message;
}

function mapLoginError(error: any): string {
  const status = getStatusCode(error);
  if (status === 401) return 'E-mail ou senha inválidos';
  if (status === 403) return 'Conta aguardando aprovação do administrador';
  return 'Erro ao fazer login';
}

function mapRegisterError(error: any): string {
  const status = getStatusCode(error);
  const apiMessage = getApiMessage(error);

  if (status === 400 && apiMessage?.toLowerCase().includes('email')) {
    return 'E-mail já cadastrado';
  }

  return 'Erro ao criar conta';
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: false,
  error: null,

  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authApi.login({ email, password });
      localStorage.setItem('token', response.token);
      set({
        user: response.user,
        token: response.token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error: any) {
      set({
        error: mapLoginError(error),
        isLoading: false,
      });
      throw error;
    }
  },

  register: async (name, email, password) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authApi.register({ name, email, password });
      set({
        isLoading: false,
      });

      return response;
    } catch (error: any) {
      set({
        error: mapRegisterError(error),
        isLoading: false,
      });
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      error: null,
    });
  },

  checkAuth: async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      set({ isAuthenticated: false });
      return;
    }

    try {
      const user = await authApi.getCurrentUser();
      set({ user, token, isAuthenticated: true });
    } catch {
      localStorage.removeItem('token');
      set({
        user: null,
        token: null,
        isAuthenticated: false,
      });
    }
  },

  clearError: () => set({ error: null }),
}));
